import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Client wrappers for the Phase 6 quote builder callables (functions/quoting).
 * All quote writes happen server-side; Firestore rules deny client writes.
 */
const call = async (name, data) => {
  const fn = httpsCallable(getFunctions(), name);
  const result = await fn(data);
  return result.data;
};

export const getQuoteSettings = () => call('getQuoteSettings', {});
export const saveQuoteSettings = (settings) => call('saveQuoteSettings', { settings });
export const buildQuoteDraft = ({ customerId, opportunityId, answers }) =>
  call('buildQuoteDraft', { customerId, opportunityId: opportunityId || null, answers });
export const updateQuoteDraft = ({ quoteId, edits, notes, status }) =>
  call('updateQuoteDraft', { quoteId, edits, notes, status });
export const refreshQuoteStock = (quoteId) => call('refreshQuoteStock', { quoteId });
export const rankQuoteWithAI = (quoteId) => call('rankQuoteWithAI', { quoteId });

export const QUESTION_KEYS = ['need', 'timing', 'budget', 'productFit'];

export const SECTION_META = {
  reorder: { label: 'Reorders', description: 'All Season styles this customer bought before (replenishable).' },
  seasonal: { label: 'This season', description: 'Season styles for the requested window — one-time, while supplies last.' },
  core: { label: 'New core styles', description: 'All Season styles that fit the request and are new to this customer.' },
};

export const STOCK_META = {
  in_stock: { label: 'In stock', className: 'bg-green-100 text-green-800' },
  backorder: { label: 'Backorder', className: 'bg-yellow-100 text-yellow-800' },
  short: { label: 'Short', className: 'bg-orange-100 text-orange-800' },
  unavailable: { label: 'Unavailable', className: 'bg-red-100 text-red-800' },
};

export const STOCK_SOURCE_LABELS = {
  live: 'Live ApparelMagic stock',
  mixed: 'Live stock (some styles from the last catalog sync)',
  synced: 'Stock from the last catalog sync',
};

/** Empty answers shaped like the server's normalizeAnswers() output. */
export const emptyAnswers = () => ({
  need: { choices: [], other: '' },
  timing: { choices: [], other: '', date: '' },
  budget: { choices: [], other: '', amount: '', units: '' },
  productFit: { choices: [], other: '' },
});

/** Toggle a choice, respecting single-select topics. */
export function toggleChoice(answer, optionId, multi) {
  const has = answer.choices.includes(optionId);
  if (multi) {
    return { ...answer, choices: has ? answer.choices.filter(id => id !== optionId) : [...answer.choices, optionId] };
  }
  return { ...answer, choices: has ? [] : [optionId] };
}

/** Request payload: numbers parsed, blanks dropped. */
export function answersPayload(answers) {
  const num = (value) => {
    const parsed = Number(value);
    return value === '' || value === null || !Number.isFinite(parsed) ? null : parsed;
  };
  return {
    need: { choices: answers.need.choices, other: answers.need.other },
    timing: { choices: answers.timing.choices, other: answers.timing.other, date: answers.timing.date || null },
    budget: { choices: answers.budget.choices, other: answers.budget.other,
      amount: num(answers.budget.amount), units: num(answers.budget.units) },
    productFit: { choices: answers.productFit.choices, other: answers.productFit.other },
  };
}

/** Recompute a line client-side for instant feedback; the server recalculates on save. */
export function previewLine(line, qtyBySku) {
  const sizes = line.sizes.map(size => ({ ...size, qty: Math.max(0, Math.floor(Number(qtyBySku[size.skuId] ?? size.qty) || 0)) }));
  const qty = sizes.reduce((sum, size) => sum + size.qty, 0);
  return { ...line, sizes, qty, amount: Math.round(qty * Number(line.unitPrice || 0) * 100) / 100 };
}
